import {
  CIRCULAR,
  MAX_DEPTH,
  NEVER_REDACTED_KEYS,
  NEVER_REDACTED_PATHS,
  REDACTED,
  REDACTED_DEPTH,
  SENSITIVE_KEY_FRAGMENTS,
  SENSITIVE_SUBTREE_PATHS,
  STRING_PATTERNS,
} from './redaction.const';

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key);

  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

export function isSensitiveSubtree(path: string): boolean {
  return SENSITIVE_SUBTREE_PATHS.some(
    (declared) => path === declared || path.endsWith(`.${declared}`),
  );
}

export function redactText(value: string): string {
  return STRING_PATTERNS.reduce(
    (redacted, [pattern, replacement]) => redacted.replace(pattern, replacement),
    value,
  );
}

function isPlainValue(value: unknown): boolean {
  return value === null || (typeof value !== 'object' && typeof value !== 'function');
}

function redactError(error: Error): Record<string, unknown> {
  return {
    kind: error.name,
    message: redactText(error.message),
    stack: error.stack ? redactText(error.stack) : undefined,
  };
}

function redactObject(
  source: Record<string, unknown>,
  path: string,
  depth: number,
  seen: WeakSet<object>,
): Record<string, unknown> {
  const target: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(source)) {
    const keyPath = path ? `${path}.${key}` : key;

    if (NEVER_REDACTED_PATHS.has(keyPath) || NEVER_REDACTED_KEYS.has(key)) {
      target[key] = isPlainValue(value) ? value : redactValue(value, keyPath, depth + 1, seen);
      continue;
    }

    if (isSensitiveSubtree(keyPath) || isSensitiveKey(key)) {
      target[key] = REDACTED;
      continue;
    }

    target[key] = redactValue(value, keyPath, depth + 1, seen);
  }

  return target;
}

export function redactValue(
  value: unknown,
  path = '',
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (typeof value === 'string') {
    return redactText(value);
  }

  if (typeof value === 'function' || typeof value === 'symbol') {
    return undefined;
  }

  if (isPlainValue(value) || value instanceof Date) {
    return value;
  }

  if (depth > MAX_DEPTH) {
    return REDACTED_DEPTH;
  }

  const object = value as object;

  if (seen.has(object)) {
    return CIRCULAR;
  }

  if (value instanceof Error) {
    return redactError(value);
  }

  seen.add(object);

  const redacted = Array.isArray(value)
    ? value.map((item) => redactValue(item, path, depth + 1, seen))
    : redactObject(value as Record<string, unknown>, path, depth, seen);

  seen.delete(object);

  return redacted;
}

export function redactAttributes(attributes: Record<string, unknown>): Record<string, unknown> {
  return redactObject(attributes, '', 0, new WeakSet());
}
